import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiDollarSign, FiShoppingBag, FiTruck, FiCheckCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import useReportData from "../../useReportData";

const PORegisterFulfillmentReport = () => {
  const gridRef = useRef(null);
  const [data] = useReportData("PoFulfillment");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalPoValue = data.reduce((s, r) => s + (r.orderedTotalValue || 0), 0);
    const fullyReceivedCount = data.filter((r) => r.status === "Fully Received & Closed").length;
    const inTransitOrPendingCount = data.filter(
      (r) => r.status === "In-Transit" || r.status === "Partially Received",
    ).length;

    return {
      totalPoValue,
      totalOrders: data.length,
      fullyReceivedCount,
      inTransitOrPendingCount,
    };
  }, [data]);

  // Unique projects
  const uniqueProjects = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.project).filter(Boolean)));
  }, [data]);

  // Status Chip mapping helper
  const getFulfillmentChipType = (status) => {
    if (status === "Fully Received & Closed") {
      return "success";
    }
    if (status === "Partially Received") {
      return "warning";
    }
    if (status === "In-Transit") {
      return "info";
    }
    if (status === "Awaiting Approval") {
      return "warning";
    }
    return "default";
  };

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "PO Number",
        field: "poNumber",
        colId: "poNumber",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (p) => (
          <span
            style={{
              fontWeight: 700,
              fontFamily: "monospace",
              color: "var(--color-primary, #0b6462)",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "PO Date",
        field: "poDate",
        colId: "poDate",
        flex: 1,
        minWidth: 110,
      },
      {
        headerName: "Delivery Target Date",
        field: "deliveryDate",
        colId: "deliveryDate",
        flex: 1.1,
        minWidth: 130,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#475569" }}>{p.value}</span>,
      },
      {
        headerName: "Vendor / Supplier Name",
        field: "vendorName",
        colId: "vendorName",
        flex: 1.7,
        minWidth: 190,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>,
      },
      {
        headerName: "Project / Site Name",
        field: "project",
        colId: "project",
        flex: 1.4,
        minWidth: 160,
      },
      {
        headerName: "Material Category & Items Ordered",
        field: "itemsOrdered",
        colId: "itemsOrdered",
        flex: 2,
        minWidth: 240,
        cellRenderer: (p) => (
          <div>
            <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "13px" }}>{p.value}</div>
            <small style={{ color: "#64748b" }}>Category: {p.data?.materialCategory}</small>
          </div>
        ),
      },
      {
        headerName: "Ordered Total Value (PKR)",
        field: "orderedTotalValue",
        colId: "orderedTotalValue",
        flex: 1.4,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0b6462" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Received Qty (GRNs)",
        field: "receivedQty",
        colId: "receivedQty",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "#16a34a" }}>
            {p.value !== undefined ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Pending / Short Qty",
        field: "pendingQty",
        colId: "pendingQty",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => {
          const qty = p.value || 0;
          return (
            <span
              style={{
                fontWeight: 600,
                color: qty > 0 ? "#dc2626" : "#64748b",
              }}
            >
              {Number(qty).toLocaleString()}
            </span>
          );
        },
      },
      {
        headerName: "PO Fulfillment Status",
        field: "status",
        colId: "status",
        flex: 1.5,
        minWidth: 170,
        cellRenderer: (p) => <StatusChip status={p.value} type={getFulfillmentChipType(p.value)} />,
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.poNumber && row.poNumber.toLowerCase().includes(q)) ||
          (row.vendorName && row.vendorName.toLowerCase().includes(q)) ||
          (row.project && row.project.toLowerCase().includes(q)) ||
          (row.itemsOrdered && row.itemsOrdered.toLowerCase().includes(q)) ||
          (row.materialCategory && row.materialCategory.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (statusFilter !== "all" && row.status !== statusFilter) {
        return false;
      }

      if (projectFilter !== "all" && row.project !== projectFilter) {
        return false;
      }

      if (dateRange.from && row.poDate && row.poDate < dateRange.from) {
        return false;
      }
      if (dateRange.to && row.poDate && row.poDate > dateRange.to) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, statusFilter, projectFilter, dateRange]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `PO_Register_Fulfillment_Tracker_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("PO register report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL COMMITTED PO VALUE"
            value={`PKR ${(kpis.totalPoValue / 1000000).toFixed(2)}M`}
            subtitle={`PKR ${kpis.totalPoValue.toLocaleString()} across orders`}
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="PURCHASE ORDERS ISSUED"
            value={`${kpis.totalOrders} POs`}
            subtitle="Operational procurement ledger"
            icon={FiShoppingBag}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="DELIVERIES IN PROGRESS"
            value={`${kpis.inTransitOrPendingCount} Orders`}
            subtitle="In-transit or partially received"
            icon={FiTruck}
            color="#d97706"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="FULLY RECEIVED & CLOSED"
            value={`${kpis.fullyReceivedCount} Orders`}
            subtitle="100% site fulfillment achieved"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="PO Register & Fulfillment"
            >
              PO Register & Fulfillment
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search PO, vendor, items..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Status Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Fulfillment Status</option>
                  <option value="Draft">Draft</option>
                  <option value="Awaiting Approval">Awaiting Approval</option>
                  <option value="In-Transit">In-Transit</option>
                  <option value="Partially Received">Partially Received</option>
                  <option value="Fully Received & Closed">Fully Received & Closed</option>
                </select>
                <label>Fulfillment Status</label>
              </div>
            </div>

            {/* Project Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                >
                  <option value="all">All Project Sites</option>
                  {uniqueProjects.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <label>Project / Site</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PORegisterFulfillmentReport;
